/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { injectable, inject } from 'inversify';
import JSZip from 'jszip';
import { eventBus } from '../../eventBus';
import { TYPES } from '@/engine/ioc';
import type { CaseDataProvider, CaseBlob } from '../CaseDataProvider';

export interface ICaseState<T = unknown> {
  version: string;
  metadata: T;
  blobs?: Array<CaseBlob>;
}

export interface ICasePersistent {
  saveState: () => Promise<ICaseState>;
  loadState: (metadata: unknown) => Promise<void>;
}

export interface IBackwardCompatibilityHandler {
  handleMetadata: (metadata: unknown, version?: string) => Promise<unknown>;
}

export interface ICasePersistanceExtension {
  registerHandlers: (registry: CasePersistenceHub) => void;
}

interface StateData {
  stateVersion: string;
  state: unknown;
}

@injectable()
export class CasePersistenceHub {
  private readonly _loadMap: Map<string, ICasePersistent> = new Map();

  private readonly _saveMap: Map<string, ICasePersistent> = new Map();

  private readonly _backwardCompatibilityHandlers: Map<string, IBackwardCompatibilityHandler> = new Map();

  private readonly _caseDataProvider: CaseDataProvider;

  public constructor(@inject(TYPES.CaseDataProvider) caseDataProvider: CaseDataProvider) {
    this._caseDataProvider = caseDataProvider;
  }

  private _registerSubject(descriptor: string, handler: ICasePersistent): void {
    if (this._loadMap.has(descriptor)) {
      throw new Error('Already registered.');
    }
    this._loadMap.set(descriptor, handler);
    this._saveMap.set(descriptor, handler);
  }

  private _registerCompatibilitySubject(descriptor: string, handler: ICasePersistent): void {
    if (this._loadMap.has(descriptor)) {
      throw new Error('Already registered.');
    }
    this._loadMap.set(descriptor, handler);
  }

  public registerSubject(
    descriptor: string,
    handler: ICasePersistent,
    ...compatibilityDescriptors: Array<string>
  ): void {
    this._registerSubject(descriptor, handler);
    for (const entry of compatibilityDescriptors) {
      this._registerCompatibilitySubject(entry, handler);
    }
  }

  private _getBackwardCompatibilityKey(descriptor: string, version?: string): string {
    return descriptor + '$v' + (version ?? '0.0');
  }

  public _registerBackwardCompatibilityHandler(
    handler: IBackwardCompatibilityHandler,
    descriptor: string,
    version?: string
  ): void {
    const key = this._getBackwardCompatibilityKey(descriptor, version);
    if (this._backwardCompatibilityHandlers.has(key)) {
      throw new Error('Already registered.');
    }
    this._backwardCompatibilityHandlers.set(key, handler);
  }

  public registerBackwardCompatibilityHandler(
    handler: IBackwardCompatibilityHandler,
    descriptor: string,
    versions?: Array<string>,
    alsoWithoutVersion = false
  ): void {
    if (!versions || alsoWithoutVersion) {
      this._registerBackwardCompatibilityHandler(handler, descriptor);
    } else {
      for (const version of versions) {
        this._registerBackwardCompatibilityHandler(handler, descriptor, version);
      }
    }
  }

  private _getBackwardCompatibilityHandler(
    descriptor: string,
    version?: string
  ): IBackwardCompatibilityHandler | undefined {
    const key = this._getBackwardCompatibilityKey(descriptor, version);
    return this._backwardCompatibilityHandlers.get(key);
  }

  public async openCase(importer: () => Promise<void>): Promise<void> {
    if (await this._caseDataProvider.hasCaseBlob('CaseStateMetadata.zip')) {
      try {
        const buffer = await this._caseDataProvider.getCaseBlob('CaseStateMetadata.zip');
        const zip = new JSZip();
        await zip.loadAsync(buffer);
        for (const [descriptor, handler] of this._loadMap.entries()) {
          const file = zip.file(descriptor + '.json');
          if (file === null) {
            console.warn('State is missing for: [' + descriptor + ']');
          } else {
            const jsonStr = await file.async('string');
            const jsonObj = JSON.parse(jsonStr);
            const version = (jsonObj as { stateVersion?: string }).stateVersion;
            const compatibility = this._getBackwardCompatibilityHandler(descriptor, version);
            const state = version !== undefined ? (jsonObj as StateData).state : jsonObj;
            const metadata = (await compatibility?.handleMetadata(state, version)) ?? state;
            await handler.loadState(metadata);
          }
        }
        eventBus.emit('testing.casePersistence.opened', null, 'open');
      } catch (e: unknown) {
        alert('This saved case package is not compatible with the current application version.');
        throw e;
      }
    } else {
      try {
        await importer();
        eventBus.emit('testing.casePersistence.opened', null, 'import');
      } catch (e: unknown) {
        alert('This initial case package is not compatible with the current application version.');
        throw e;
      }
    }
  }

  private async _saveCase(): Promise<Array<CaseBlob>> {
    const zip = new JSZip();
    const blobs = new Array<CaseBlob>();
    for (const [descriptor, handler] of this._saveMap.entries()) {
      const state = await handler.saveState();
      if (state.blobs !== undefined) {
        blobs.push(...state.blobs);
      }
      const stateData: StateData = {
        stateVersion: state.version,
        state: state.metadata
      };
      const json = JSON.stringify(stateData);
      const file = new Blob([json], { type: 'application/json' });
      zip.file(descriptor + '.json', file);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    blobs.push({ descriptor: 'CaseStateMetadata.zip', blob: blob });
    return blobs;
  }

  public async saveCase(): Promise<void> {
    const blobs = await this._saveCase();
    await this._caseDataProvider.saveCase(blobs);
    eventBus.emit('testing.casePersistence.saved');
  }

  public async approveCase(reportBlob: CaseBlob): Promise<string> {
    const blobs = await this._saveCase();
    blobs.push(reportBlob);
    const uri = await this._caseDataProvider.approveCase(blobs);
    eventBus.emit('testing.casePersistence.approved');
    return uri;
  }

  public async rejectCase(): Promise<string> {
    //const blobs = await this._saveCase(); - do not save
    const uri = await this._caseDataProvider.rejectCase();
    eventBus.emit('testing.casePersistence.rejected');
    return uri;
  }
}
